import { createReducer, on } from '@ngrx/store';
import { AlarmManagementModel } from '@models/alarm-management.model';
import { AlarmManagementAction } from '@states/alarm-mangment/alarm-management.action-types';

export interface AlarmManagementState {
  activeAlarms: AlarmManagementModel.AlarmData[];
  canceledAlarms: AlarmManagementModel.AlarmData[];
  allAlarms: AlarmManagementModel.AlarmData[];
  selectedAlarm: AlarmManagementModel.AlarmData | null;
  checkResult: AlarmManagementModel.AlarmCheckResponse | null;
  currentFilter: AlarmManagementModel.AlarmFilter;
  isLoading: boolean;
  error: string | null;
  // New state for alert instances
  alarmsWithAlerts: AlarmManagementModel.AlarmWithAlerts[];
  alarmsWithAlertsTotal: number;
  alarmAlertInstances: { [alarmId: string]: AlarmManagementModel.AlertInstanceSummary[] };
  alarmAlertInstancesTotal: { [alarmId: string]: number };
  noonlightActivities: { [alertInstanceId: number]: AlarmManagementModel.NoonlightActivityLog[] };
  rejectedAlerts: AlarmManagementModel.RejectedAlert[];
  rejectedAlertsTotal: number;
  rejectedAlertsLimit: number;
  rejectedAlertsOffset: number;
  rejectedAlertsLoading: boolean;
  selectedTab: AlarmManagementModel.SelectedTab;
  expandedAlarmIds: string[];
  alertInstancesLoading: { [alarmId: string]: boolean };
  noonlightActivityLoading: { [alertInstanceId: number]: boolean };
}

export const initialState: AlarmManagementState = {
  activeAlarms: [],
  canceledAlarms: [],
  allAlarms: [],
  selectedAlarm: null,
  checkResult: null,
  currentFilter: {},
  isLoading: false,
  error: null,
  // New state for alert instances
  alarmsWithAlerts: [],
  alarmsWithAlertsTotal: 0,
  alarmAlertInstances: {},
  alarmAlertInstancesTotal: {},
  noonlightActivities: {},
  rejectedAlerts: [],
  rejectedAlertsTotal: 0,
  rejectedAlertsLimit: 50,
  rejectedAlertsOffset: 0,
  rejectedAlertsLoading: false,
  selectedTab: 'active',
  expandedAlarmIds: [],
  alertInstancesLoading: {},
  noonlightActivityLoading: {},
};

export const alarmManagementReducer = createReducer(
  initialState,

  // Reset State
  on( AlarmManagementAction.resetToInitialState, () => ({
    ...initialState,
  })),

  // Loading State
  on( AlarmManagementAction.setAlarmsLoading, (state, { isLoading }) => ({
    ...state,
    isLoading,
    error: isLoading ? null : state.error,
  })),

  // Active Alarms
  on( AlarmManagementAction.getActiveAlarmsSuccess, (state, { activeAlarms }) => ({
    ...state,
    activeAlarms,
    error: null,
  })),
  on( AlarmManagementAction.getActiveAlarmsFail, (state) => ({
    ...state,
    error: 'Failed to load active alarms',
  })),

  // Canceled Alarms
  on( AlarmManagementAction.getCanceledAlarmsSuccess, (state, { canceledAlarms }) => ({
    ...state,
    canceledAlarms,
    error: null,
  })),
  on( AlarmManagementAction.getCanceledAlarmsFail, (state) => ({
    ...state,
    error: 'Failed to load canceled alarms',
  })),

  // Alarm History
  on( AlarmManagementAction.getAlarmHistorySuccess, (state, { alarms }) => ({
    ...state,
    allAlarms: alarms,
    error: null,
  })),
  on( AlarmManagementAction.getAlarmHistoryFail, (state) => ({
    ...state,
    error: 'Failed to load alarm history',
  })),

  // Create Alarm
  on( AlarmManagementAction.createAlarmSuccess, (state, { alarmId }) => ({
    ...state,
    error: null,
  })),
  on( AlarmManagementAction.createAlarmFail, (state) => ({
    ...state,
    error: 'Failed to create alarm',
  })),

  // Cancel Alarm
  on( AlarmManagementAction.cancelAlarmSuccess, (state) => ({
    ...state,
    error: null,
  })),
  on( AlarmManagementAction.cancelAlarmFail, (state) => ({
    ...state,
    error: 'Failed to cancel alarm',
  })),

  // Check Active Alarm
  on( AlarmManagementAction.checkActiveAlarmSuccess, (state, { checkResult }) => ({
    ...state,
    checkResult,
    error: null,
  })),
  on( AlarmManagementAction.checkActiveAlarmFail, (state) => ({
    ...state,
    error: 'Failed to check active alarm',
  })),

  // UI State
  on( AlarmManagementAction.setSelectedAlarm, (state, { alarm }) => ({
    ...state,
    selectedAlarm: alarm,
  })),
  on( AlarmManagementAction.setAlarmFilter, (state, { filter }) => ({
    ...state,
    currentFilter: filter,
  })),

  // Alarms with Alerts
  on( AlarmManagementAction.getAlarmsWithAlertsSuccess, (state, { response }) => ({
    ...state,
    alarmsWithAlerts: response.data,
    alarmsWithAlertsTotal: response.total,
    error: null,
  })),
  on( AlarmManagementAction.getAlarmsWithAlertsFail, (state) => ({
    ...state,
    error: 'Failed to load alarms with alerts',
  })),

  // Alarm Alert Instances
  on( AlarmManagementAction.getAlarmAlertInstances, (state, { alarmId }) => ({
    ...state,
    alertInstancesLoading: { ...state.alertInstancesLoading, [alarmId]: true },
  })),
  on( AlarmManagementAction.getAlarmAlertInstancesSuccess, (state, { alarmId, response }) => ({
    ...state,
    alarmAlertInstances: { ...state.alarmAlertInstances, [alarmId]: response.data },
    alarmAlertInstancesTotal: { ...state.alarmAlertInstancesTotal, [alarmId]: response.total },
    alertInstancesLoading: { ...state.alertInstancesLoading, [alarmId]: false },
    error: null,
  })),
  on( AlarmManagementAction.getAlarmAlertInstancesFail, (state, { alarmId }) => ({
    ...state,
    alertInstancesLoading: { ...state.alertInstancesLoading, [alarmId]: false },
    error: 'Failed to load alert instances',
  })),

  // Noonlight Activity
  on( AlarmManagementAction.getAlertNoonlightActivity, (state, { alertInstanceId }) => ({
    ...state,
    noonlightActivityLoading: { ...state.noonlightActivityLoading, [alertInstanceId]: true },
  })),
  on( AlarmManagementAction.getAlertNoonlightActivitySuccess, (state, { alertInstanceId, activities }) => ({
    ...state,
    noonlightActivities: { ...state.noonlightActivities, [alertInstanceId]: activities },
    noonlightActivityLoading: { ...state.noonlightActivityLoading, [alertInstanceId]: false },
    error: null,
  })),
  on( AlarmManagementAction.getAlertNoonlightActivityFail, (state, { alertInstanceId }) => ({
    ...state,
    noonlightActivityLoading: { ...state.noonlightActivityLoading, [alertInstanceId]: false },
    error: 'Failed to load Noonlight activity',
  })),

  // Rejected Alerts
  on( AlarmManagementAction.startGetRejectedAlerts, (state, { filter }) => ({
    ...state,
    rejectedAlertsLoading: true,
    rejectedAlertsOffset: filter?.offset ?? state.rejectedAlertsOffset,
    rejectedAlertsLimit: filter?.limit ?? state.rejectedAlertsLimit,
  })),
  on( AlarmManagementAction.getRejectedAlertsSuccess, (state, { response }) => ({
    ...state,
    rejectedAlerts: response.data,
    rejectedAlertsTotal: response.total,
    rejectedAlertsLimit: response.limit,
    rejectedAlertsOffset: response.offset,
    rejectedAlertsLoading: false,
    error: null,
  })),
  on( AlarmManagementAction.getRejectedAlertsFail, (state) => ({
    ...state,
    rejectedAlertsLoading: false,
    error: 'Failed to load rejected alerts',
  })),

  // Tab and Expanded State
  on( AlarmManagementAction.setSelectedTab, (state, { tab }) => ({
    ...state,
    selectedTab: tab,
  })),
  on( AlarmManagementAction.toggleExpandedAlarm, (state, { alarmId }) => ({
    ...state,
    expandedAlarmIds: state.expandedAlarmIds.includes(alarmId)
      ? state.expandedAlarmIds.filter(id => id !== alarmId)
      : [...state.expandedAlarmIds, alarmId],
  })),
  on( AlarmManagementAction.setExpandedAlarms, (state, { alarmIds }) => ({
    ...state,
    expandedAlarmIds: alarmIds,
  })),
  on( AlarmManagementAction.clearExpandedAlarms, (state) => ({
    ...state,
    expandedAlarmIds: [],
  }))
);
